import { useId } from 'react';

import { CollapsibleSection } from 'cpool-components/general/CollapsibleSection';
import { APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { NotificationCountBadge } from './NotificationCountBadge.tsx';

type Props = {
    applicant: 1 | 2;
    children: React.ReactNode;
    collapsible: boolean;
    defaultOpen?: boolean;
    errorCount?: number;
    style?: React.CSSProperties;
    testId?: string;
};

export const ApplicantCollapsible: React.FC<Props> = ({
    applicant,
    children,
    collapsible,
    defaultOpen = true,
    errorCount = 0,
    style,
    testId,
}) => {
    const { t } = useTranslation();
    const errorDescriptionId = useId();

    if (!collapsible) return <>{children}</>;

    const attribution =
        applicant === 1 ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1 : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2;
    const title = t(`APPLICANT_ATTRIBUTION.${attribution}`);

    return (
        <>
            {errorCount > 0 && (
                <span className="sr-only" id={errorDescriptionId}>
                    {t('CUSTOMER_DATA_ERROR_COUNT', { count: errorCount })}
                </span>
            )}
            <CollapsibleSection
                aria-describedby={errorCount > 0 ? errorDescriptionId : undefined}
                bgClassNames="rounded bg-white border-l-2 border-blue p-0"
                defaultOpen={defaultOpen}
                headerClassNames="h-[45px] px-5"
                keepMounted
                style={style}
                testId={testId}
                title={
                    errorCount > 0 ? (
                        <span className="flex items-center gap-2">
                            {title}
                            <NotificationCountBadge ariaHidden count={errorCount} />
                        </span>
                    ) : (
                        title
                    )
                }
                titleClassNames="text-sm font-bold text-midnight"
            >
                {/* no horizontal padding: the FormCards inside bring their own, which keeps the
                    inset identical whether or not a collapsible wraps them */}
                <div className="space-y-2 pb-5">{children}</div>
            </CollapsibleSection>
        </>
    );
};
